import { COMN_CODE } from "../../constants";

export type ResourceSnapshot = {
    key: string;
    data: any[];
    options: { value: any; label: any }[];
    count: number;
    updated: number;
    area: string;
    comnCd?: string;
};

export type ResourceNotification = {
    value: any[];
    updated: number;
};

/**
 * 리소스 원본 데이터를 모든 옵션 소비자가 사용하는 value/label 구조로 변환한다.
 */
export const makeResourceOptions = (area: string, data: any[] = []) => {
    const code = COMN_CODE[area];
    if (!code) return data;

    return data.map((item: any) => {
        const label = Array.isArray(code.label) ? code.label.map((key: string) => item[key]).join(", ") : item[code.label];
        return { value: item[code.value], label };
    });
};

/**
 * IndexedDB 레코드를 메모리·옵션 훅이 공유하는 완전한 리소스 스냅샷으로 변환한다.
 */
export const createResourceSnapshot = (area: string, comnCd: string | undefined, record: any): ResourceSnapshot => {
    const data = Array.isArray(record?.value) ? record.value : [];
    return {
        key: record.key,
        data,
        options: makeResourceOptions(area, data),
        count: data.length,
        updated: Number(record.updated),
        area,
        comnCd,
    };
};

/**
 * 레거시 전체 레코드와 숫자·Date 알림을 모두 숫자형 갱신 버전으로 정규화한다.
 */
export const getResourceVersion = (notification: any): number | null => {
    const version = notification?.updated ?? notification;
    if (version instanceof Date) return version.getTime();
    if (typeof version === "number" && Number.isFinite(version)) return version;

    const parsed = Number(version);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * 같은 밀리초에 연속 저장되어도 이전 값보다 큰 갱신 버전을 생성한다.
 */
export const getNextResourceVersion = (previous?: any): number => {
    const previousVersion = getResourceVersion(previous) ?? 0;
    return Math.max(Date.now(), previousVersion + 1);
};

/**
 * 동일 리소스의 동시 갱신 요청을 하나의 완료 Promise로 합친다.
 */
export const runResourceRefresh = <T>(
    pending: Map<string, Promise<T>>,
    key: string,
    task: () => Promise<T>,
): Promise<T> => {
    const current = pending.get(key);
    if (current) return current;

    const refresh = Promise.resolve()
        .then(task)
        .finally(() => {
            if (pending.get(key) === refresh) pending.delete(key);
        });
    pending.set(key, refresh);
    return refresh;
};

/**
 * 기존 useOptions 소비자와 갱신 알림 소비자가 함께 읽을 수 있는 Recoil 값을 생성한다.
 */
export const createResourceNotification = (resource: ResourceSnapshot): ResourceNotification => {
    return { value: resource.data, updated: resource.updated };
};
